import React, { useMemo, useState } from "react";
import { Modal, Radio, Input, Alert, Typography, Space } from "antd";
import { postAPI, displayMessage } from "../../utils/common";
import { BOOKING_ACTION } from "../../constants/api";
import { CURRENCY } from "../../constants/constent";
import {
  ERROR_MSG_TYPE,
  SUCCESS_MESSAGE,
} from "../../constants/messages";
import LS_SERVICE from "../../utils/localStorage";

const { Text } = Typography;

/**
 * Reusable "Cancel Booking" confirmation modal.
 *
 * Asks the admin how to refund:
 *   - Full Refund (refund_mode = "full")
 *   - Apply Cancellation Policy (refund_mode = "policy") — only offered when a
 *     cancellation policy is enabled in Global Settings.
 *
 * Props:
 *   visible      : boolean
 *   booking      : the booking object (needs id, advance_amount, checkin_date, tnx)
 *   onClose      : () => void
 *   onConfirmed  : () => void  (called after a successful cancel)
 */
const CancelBookingModal = ({ visible, booking, onClose, onConfirmed }) => {
  const setting = useMemo(() => LS_SERVICE.get("setting") || {}, []);
  const policyEnabled = !!setting.cancellation_policy_enable;

  const [refundMode, setRefundMode] = useState(policyEnabled ? "policy" : "full");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Amount the guest actually paid: prefer successful Razorpay txn, else advance.
  const paidAmount = useMemo(() => {
    if (!booking) return 0;
    const rzp = (booking.tnx || []).find(
      (t) => t.razorpay_payment_id && t.status === "Successful"
    );
    return rzp
      ? parseFloat(rzp.amount || 0)
      : parseFloat(booking.advance_amount || 0);
  }, [booking]);

  // Policy-based refund preview (mirrors the backend calculator).
  const policyPreview = useMemo(() => {
    if (!policyEnabled) {
      return { amount: paidAmount, pct: 100, tier: "disabled" };
    }
    const checkin = booking?.checkin_date
      ? new Date(booking.checkin_date)
      : null;
    const hoursBefore = checkin
      ? (checkin.getTime() - Date.now()) / 36e5
      : -1;

    const freeHours = Number(setting.cancellation_free_hours || 0);
    const partialHours = Number(setting.cancellation_partial_hours || 0);
    const partialPct = Number(setting.cancellation_partial_percentage || 0);
    const noRefundPct = Number(setting.cancellation_no_refund_percentage || 0);

    let pct, tier;
    if (hoursBefore >= freeHours) {
      pct = 100;
      tier = "free";
    } else if (hoursBefore >= partialHours) {
      pct = partialPct;
      tier = "partial";
    } else {
      pct = noRefundPct;
      tier = "no-refund";
    }
    return { amount: (paidAmount * pct) / 100, pct, tier };
  }, [policyEnabled, paidAmount, booking, setting]);

  const estimatedRefund =
    refundMode === "full" ? paidAmount : policyPreview.amount;

  const money = (n) => `${CURRENCY} ${Number(n || 0).toFixed(2)}`;

  const handleConfirm = () => {
    if (!booking?.id) return;
    setLoading(true);
    const body = {
      id: booking.id,
      action_type: "reject",
      reason: reason || "",
      cancel_by: LS_SERVICE.get("user_type") || "",
      refund_mode: refundMode,
    };
    const successFn = (data) => {
      setLoading(false);
      if (data.status === true) {
        displayMessage(SUCCESS_MESSAGE, data.message);
        setReason("");
        onConfirmed && onConfirmed();
        onClose && onClose();
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };
    const errorFn = () => {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to cancel booking");
    };
    postAPI(BOOKING_ACTION, body, successFn, errorFn);
  };

  return (
    <Modal
      title="Cancel Booking"
      open={visible}
      onCancel={() => !loading && onClose && onClose()}
      onOk={handleConfirm}
      okText="Confirm Cancel"
      okButtonProps={{ danger: true, loading: loading, disabled: loading }}
      cancelButtonProps={{ disabled: loading }}
      maskClosable={!loading}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Text>How would you like to refund this booking?</Text>

        <Radio.Group
          onChange={(e) => setRefundMode(e.target.value)}
          value={refundMode}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <Radio value="full">
            <b>Full Refund</b> — refund the entire paid amount ({money(paidAmount)})
          </Radio>
          {policyEnabled && (
            <Radio value="policy">
              <b>Apply Cancellation Policy</b> — refund {policyPreview.pct}% (
              {policyPreview.tier} tier)
            </Radio>
          )}
        </Radio.Group>

        {policyEnabled && setting.cancellation_policy_text && (
          <Alert
            type="info"
            showIcon
            message="Cancellation Policy"
            description={setting.cancellation_policy_text}
          />
        )}

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Amount Paid</span>
            <b>{money(paidAmount)}</b>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <span>Estimated Refund</span>
            <b style={{ color: "#198754" }}>{money(estimatedRefund)}</b>
          </div>
        </div>

        <div>
          <Text>Reason for cancellation:</Text>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter cancellation reason..."
            style={{ marginTop: 6 }}
          />
        </div>
      </Space>
    </Modal>
  );
};

export default CancelBookingModal;
